import { Box, Button, Drawer, FormControl, FormControlLabel, IconButton, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import { BuyWalletPoint } from '../../store/crm/crm';
import { useFormik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import CustomButtoncolor from '../custom-button/customColorButton';
import Waveloading from '../../pages/loading/Waveloading';

const BURGUNDY = '#A72C68';
const BURGUNDY_DARK = '#921F58';
const PINK_BG = '#FAEFF5';
const PINK_BORDER = '#F0CDDB';

const labelStyle = {
    color: BURGUNDY,
    fontSize: '12px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    mb: '6px',
};

export const inputStyle = {
    background: '#fff',
    borderRadius: '10px',
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        '& fieldset': { borderColor: PINK_BORDER },
        '&:hover fieldset': { borderColor: BURGUNDY },
        '&.Mui-focused fieldset': { borderColor: BURGUNDY, borderWidth: '1.5px' },
    },
};

const BuyWallet = ({ customer_id, reFetchData }) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('Cash');

    const handlePaymentMethodChange = (event) => {
        setSelectedPaymentMethod(event.target.value);
    };

    const validate = (values) => {
        const errors = {};
        if (!values.amount) errors.amount = 'amount is required';
        return errors;
    };

    const formik = useFormik({
        initialValues: { amount: '', type: '', paymentDetails: '' },
        validate,
        onSubmit: async (values) => {
            try {
                const finalPayload = {
                    amount: formik.values.amount,
                    type: selectedPaymentMethod,
                    ...(selectedPaymentMethod !== 'Cash' && { paymentDetails: formik.values.paymentDetails }),
                    customer_id,
                };
                setLoading(true);
                let { payload } = await dispatch(BuyWalletPoint(finalPayload));
                if (payload?.success === 'true') {
                    toast.success('Wallet update successfully!');
                    handleClose();
                    reFetchData && reFetchData();
                    setLoading(false);
                } else {
                    setLoading(false);
                    if (payload?.data?.errors) {
                        toast.error(payload?.data?.errors || 'Something went wrong.');
                    }
                }
                handleClose();
            } catch (error) {
                setLoading(false);
            }
        },
    });

    const paymentOptions = [
        { value: 'Cash', label: 'Cash' },
        { value: 'E-Transfer', label: 'E-Transfer' },
        { value: 'Card', label: 'Credit/Debit Card (External device)' },
    ];

    const showDetailsField = selectedPaymentMethod === 'E-Transfer' || selectedPaymentMethod === 'Card';
    const detailsLabel = selectedPaymentMethod === 'E-Transfer' ? 'E-Transfer/Transaction ID' : 'Transaction ID/Card Number';

    return (
        <>
            <CustomButtoncolor
                handleClick={handleOpen}
                mainColor={'#219653'}
                size="md"
                val="Buy Wallet Point"
                style={{
                    padding: '7px 5px',
                    fontSize: '18px',
                    minWidth: 'max-content',
                    borderRadius: '6px!important',
                    width: '100%',
                    '@media (minWidth: 420px)': { width: 'auto' },
                }}
            />

            <Drawer
                anchor="right"
                open={open}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 480, md: 540 },
                        maxWidth: '100vw',
                        background: '#fff',
                        boxShadow: '-12px 0 40px rgba(0,0,0,0.10)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{
                    position: 'relative',
                    display: 'flex', alignItems: 'flex-start', gap: 1.25,
                    px: 2.5, py: 2,
                    borderBottom: `1px solid ${PINK_BORDER}`,
                    flexShrink: 0,
                }}>
                    <Box sx={{
                        width: 42, height: 42, borderRadius: '10px',
                        background: PINK_BG, border: `1px solid ${PINK_BORDER}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}>
                        <PaidOutlinedIcon sx={{ color: BURGUNDY, fontSize: 22 }} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 3 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '17px', color: '#1a1a2e', lineHeight: 1.2, fontFamily: 'helvetica-lt-std-bold' }}>
                            Buy Wallet Points
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#6B7280', mt: '2px' }}>
                            Top up the customer's wallet balance.
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleClose}
                        sx={{
                            position: 'absolute', top: 10, right: 10,
                            color: '#6B7280', borderRadius: '8px',
                            width: 32, height: 32,
                            border: `1px solid ${PINK_BORDER}`,
                            '&:hover': { background: PINK_BG, color: BURGUNDY },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>

                {/* Body */}
                <Box sx={{ flex: 1, overflowY: 'auto', position: 'relative', p: { xs: 2, sm: 3 } }}>
                    {loading && <Waveloading cover />}

                    <FormControl fullWidth sx={{ mb: 2.5 }}>
                        <Typography sx={labelStyle}>Amount</Typography>
                        <TextField
                            fullWidth
                            size="small"
                            name="amount"
                            sx={inputStyle}
                            placeholder="$ 0.00"
                            value={formik.values.amount}
                            onChange={formik.handleChange}
                        />
                    </FormControl>

                    <Typography sx={labelStyle}>Payment Method</Typography>
                    <RadioGroup
                        value={selectedPaymentMethod}
                        onChange={handlePaymentMethodChange}
                        sx={{ gap: 1 }}
                    >
                        {paymentOptions.map((opt) => {
                            const active = selectedPaymentMethod === opt.value;
                            return (
                                <FormControlLabel
                                    key={opt.value}
                                    value={opt.value}
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#D08CAA',
                                                '&.Mui-checked': { color: BURGUNDY },
                                            }}
                                        />
                                    }
                                    label={opt.label}
                                    sx={{
                                        m: 0,
                                        px: 1.5, py: 0.5,
                                        borderRadius: '10px',
                                        border: `1px solid ${active ? BURGUNDY : PINK_BORDER}`,
                                        background: active ? PINK_BG : '#fff',
                                        transition: 'all 0.15s ease',
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: '13.5px',
                                            color: '#1a1a2e',
                                            fontWeight: active ? 700 : 500,
                                        },
                                        '&:hover': { borderColor: BURGUNDY, background: PINK_BG },
                                    }}
                                />
                            );
                        })}
                    </RadioGroup>

                    {showDetailsField && (
                        <FormControl fullWidth sx={{ mt: 2.5 }}>
                            <Typography sx={labelStyle}>{detailsLabel}</Typography>
                            <TextField
                                fullWidth
                                size="small"
                                name="paymentDetails"
                                sx={inputStyle}
                                placeholder="Enter reference"
                                value={formik.values.paymentDetails}
                                onChange={formik.handleChange}
                            />
                        </FormControl>
                    )}
                </Box>

                {/* Footer */}
                <Box sx={{
                    display: 'flex', justifyContent: 'flex-end', gap: 1,
                    px: { xs: 2, sm: 3 }, py: 2,
                    borderTop: `1px solid ${PINK_BORDER}`,
                    flexShrink: 0,
                }}>
                    <Button
                        onClick={handleClose}
                        sx={{
                            height: 42, px: 3, borderRadius: '10px',
                            textTransform: 'capitalize', fontSize: '13px', fontWeight: 600,
                            color: '#fff', backgroundColor: '#EB5757',
                            border: '1px solid #EB5757',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#fff', color: '#EB5757', border: '1px solid #EB5757', boxShadow: 'none' },
                        }}
                    >
                        Close
                    </Button>
                    <Button
                        onClick={formik.handleSubmit}
                        sx={{
                            height: 42, px: 3, borderRadius: '10px',
                            textTransform: 'capitalize', fontSize: '13px', fontWeight: 700,
                            color: '#fff',
                            background: 'linear-gradient(135deg, #27AE60 0%, #1E8449 100%)',
                            border: '1px solid #27AE60',
                            boxShadow: 'none',
                            '&:hover': { background: '#219653', color: '#fff', border: '1px solid #27AE60', boxShadow: 'none' },
                        }}
                    >
                        Confirm
                    </Button>
                </Box>
            </Drawer>
        </>
    );
};

export default BuyWallet;
